import React, { useState, useEffect, useCallback, useRef } from 'react';
import { pickerOptions } from '../utils/pickerOptions';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { canDeleteForever } from '../utils/deleteGuard';
import { useExport } from './header/exporters';
import { formatDayMonth } from '../utils/formatDate';
import { PHONE_PLACEHOLDER } from '../constants/placeholders';
import idColumn from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import { Input as FormInput } from './FormField';
import SearchableSelect from './ui/SearchableSelect';
import RichTextEditor from './ui/RichTextEditor';
import { VehicleFormFields } from './forms/VehicleFormFields';
import Icon from './Icon';
import EmptyState from './EmptyState';
import ConfirmDialog from './ui/ConfirmDialog';
import SidebarPhotoCard from './ui/SidebarPhotoCard';
import ContactCard from './ui/ContactCard';
import ContentCard from './ContentCard';
import { uploadImage, deleteAttachment, getAttachments, resolveMediaUrl } from '../services/uploadService';
import { useNotifications } from './NotificationProvider';
import { loadGuests } from '../services/GuestService';

import { MetricCard } from './MetricCards';
import PageHeader from './PageHeader';
import DataTable from './DataTable';
import StatusBadge from './ui/StatusBadge';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusStepper, { StatusStep } from './ui/StatusStepper';
import { assignmentStatusColors, movementTypeColors } from '../constants/statusColors';
import {
  refreshVehiclesFromApi,
  refreshDriversFromApi,
  refreshAssignmentsFromApi,
  createVehicle as svcCreateVehicle,
  updateVehicle as svcUpdateVehicle,
  deleteVehicle as svcDeleteVehicle,
  createDriver as svcCreateDriver,
  updateDriver as svcUpdateDriver,
  deleteDriver as svcDeleteDriver,
  createAssignment as svcCreateAssignment,
  updateAssignment as svcUpdateAssignment,
  deleteAssignment as svcDeleteAssignment,
  getLogisticsLoadErrors,
  LOGISTICS_LOAD_STATE_EVENT,
  type LogisticsLoadKind,
} from '../services/LogisticsService';
import {
  Vehicle,
  Driver,
  GuestAssignment,
  LogisticsStats,
  VehicleType,
  AssignmentStatus,
  MovementType,
} from '../types/ui/Logistics';
import '../styles/components/LogisticsManager.css';
import { fmtId } from '../utils/jiraId';


const VEHICLE_TYPE_LABELS: Record<VehicleType, string> = {
  sedan: 'Sedan', suv: 'SUV', bus: 'Bus', van: 'Van',
  limo: 'Limousine', minibus: 'Minibus', other: 'Other',
};

type Tab = 'vehicles' | 'assignments' | 'drivers';
type AssignmentViewMode = 'table' | 'timeline';

// Mirrors App.tsx's LOGISTICS_TAB_SLUGS — used only so "back to Logistics"
// navigations here (after saving/cancelling a vehicle or driver form) land
// on the tab the user came from instead of always bouncing to the default
// /logistics/schedule redirect.
const LOGISTICS_TAB_SLUGS: Record<Tab, string> = {
  vehicles: 'vehicles',
  assignments: 'schedule',
  drivers: 'drivers',
};

// ── Blank form objects ──────────────────────────────────────────────────────

const blankVehicle = (): Omit<Vehicle, 'id'> => ({
  name: '', type: 'sedan', license_plate: '', color: '',
  capacity: 4, driver_id: '', features: [], status: 'available', notes: '',
});

const blankDriver = (): Omit<Driver, 'id'> => ({
  name: '', phone: '', email: '', vehicle_id: '',
  notes: '', emergency_contact: '', emergency_phone: '',
});

const blankAssignment = (): Omit<GuestAssignment, 'id'> => ({
  guest_name: '', guest_phone: '', vehicle_id: '', pickup_location: '',
  dropoff_location: '', pickup_time: '', dropoff_time: '', notes: '',
  status: 'pending', plus_ones: 0, movement_type: 'pickup', guest_ids: [],
});

// Read-only version of the movement-form's Pending/Confirmed/Completed
// stepper — replaces the old per-card colour dot, which carried no more
// information than the status badge already next to it and was clipping at
// the card edge. Was a hand-rolled fourth copy of the same markup; now the
// shared primitive, so it can't drift from the editable one in the header.
const STATUS_STEPS: StatusStep[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
];
const MiniStatusStepper: React.FC<{ status: AssignmentStatus }> = ({ status }) => (
  <StatusStepper
    className="status-stepper--compact"
    ariaLabel={`Status: ${status}`}
    steps={STATUS_STEPS}
    current={status}
  />
);

// ── Main Component ──────────────────────────────────────────────────────────

interface LogisticsManagerProps {
  triggerAddVehicle?: boolean;
  onAddVehicleHandled?: () => void;
  triggerAddDriver?: boolean;
  onAddDriverHandled?: () => void;
  triggerAddAssignment?: boolean;
  onAddAssignmentHandled?: () => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  vehicleAction?: 'new' | 'edit';
  vehicleId?: string;
  onVehicleNameChange?: (name: string | null) => void;
  driverAction?: 'new' | 'edit';
  driverId?: string;
  onDriverNameChange?: (name: string | null) => void;
  onAssignmentFormOpenChange?: (open: boolean, isEditing?: boolean) => void;
  assignmentStatusOverride?: string;
  onAssignmentStatusChange?: (s: string) => void;
}

const LogisticsManager: React.FC<LogisticsManagerProps> = ({ triggerAddVehicle, onAddVehicleHandled, triggerAddDriver, onAddDriverHandled, triggerAddAssignment, onAddAssignmentHandled, activeTab: activeTabProp, onTabChange, vehicleAction, vehicleId: vehicleIdProp, onVehicleNameChange, driverAction, driverId: driverIdProp, onDriverNameChange, onAssignmentFormOpenChange, assignmentStatusOverride, onAssignmentStatusChange }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();

  // LogisticsService's create/update/delete calls are fire-and-forget
  // (cache updates optimistically before the network call resolves) — a
  // rejected save has nowhere to throw to, so it reports itself here
  // instead of failing silently.
  useEffect(() => {
    const onSaveError = (e: Event) => showError((e as CustomEvent<string>).detail);
    window.addEventListener('logistics-save-error', onSaveError);
    return () => window.removeEventListener('logistics-save-error', onSaveError);
  }, [showError]);

  const [activeTabInternal, setActiveTabInternal] = useState<Tab>('assignments');
  const activeTab = (activeTabProp as Tab) ?? activeTabInternal;
  const setActiveTab = (tab: Tab) => {
    setActiveTabInternal(tab);
    onTabChange?.(tab);
  };
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [assignments, setAssignments] = useState<GuestAssignment[]>([]);
  const [assignmentView, setAssignmentView] = useState<AssignmentViewMode>('timeline');
  const [guestOptions, setGuestOptions] = useState<{ id: string; name: string }[]>([]);
  const [pickGuestId, setPickGuestId] = useState('');

  // Modal states
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);

  // Edit states
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [editingAssignment, setEditingAssignment] = useState<GuestAssignment | null>(null);

  // Form states
  const [vehicleForm, setVehicleForm] = useState(blankVehicle());
  const [driverForm, setDriverForm] = useState(blankDriver());
  const [assignmentForm, setAssignmentForm] = useState(blankAssignment());
  const [vehicleImages, setVehicleImages] = useState<{ id: string; url: string }[]>([]);
  const [vehicleImageUploading, setVehicleImageUploading] = useState(false);
  const [driverImages, setDriverImages] = useState<{ id: string; url: string }[]>([]);
  const [driverImageUploading, setDriverImageUploading] = useState(false);

  // Changing tab (the breadcrumb dropdown or the tab bar) means the user has left what they were working on:
  // whatever form was open on the old tab is discarded, so it does not stay on top of the new tab.
  const previousTab = useRef(activeTab);
  useEffect(() => {
    if (previousTab.current === activeTab) return;
    previousTab.current = activeTab;
    setShowVehicleModal(false); setShowDriverModal(false); setShowAssignmentModal(false);
    setEditingVehicle(null); setEditingDriver(null); setEditingAssignment(null);
    setVehicleForm(blankVehicle()); setDriverForm(blankDriver()); setAssignmentForm(blankAssignment());
  }, [activeTab]);
  // Delete forever is only offered on an archived record; each dialog says why if the server refuses.
  const foreverVehicle = useDeleteConfirm<Vehicle>({
    record: 'vehicle',
    perform: v => svcDeleteVehicle(v.id),
    onDone: v => {
      setVehicles(prev => prev.filter(x => x.id !== v.id));
      if (vehicleIdProp) navigate(`/logistics/${LOGISTICS_TAB_SLUGS[activeTab]}`);
    },
  });
  const foreverDriver = useDeleteConfirm<Driver>({
    record: 'driver',
    perform: d => svcDeleteDriver(d.id),
    onDone: d => {
      setDrivers(prev => prev.filter(x => x.id !== d.id));
      if (driverIdProp) navigate(`/logistics/${LOGISTICS_TAB_SLUGS[activeTab]}`);
    },
  });
  const foreverAssignment = useDeleteConfirm<GuestAssignment>({
    record: 'movement',
    perform: a => svcDeleteAssignment(a.id),
    onDone: a => setAssignments(prev => prev.filter(x => x.id !== a.id)),
  });
  type ArchiveFilter = 'active' | 'archived' | 'all';
  const [archiveFilterByTab, setArchiveFilterByTab] = useState<Record<string, ArchiveFilter>>({
    vehicles: 'active',
    drivers: 'active',
    assignments: 'active',
  });

  // 3-state (active/archived/all) matching Venues, was a plain Show/Hide
  // toggle — the dropdown is a header breadcrumb control (was inline in the
  // page body); Header.tsx owns the control, this owns the real per-tab
  // state, synced via events the same way catalog-type-filter is.
  useEffect(() => {
    const onSet = (e: Event) => {
      const { tab, value } = (e as CustomEvent).detail as { tab: string; value: ArchiveFilter };
      setArchiveFilterByTab(prev => ({ ...prev, [tab]: value }));
    };
    window.addEventListener('logistics-archived-set', onSet);
    return () => window.removeEventListener('logistics-archived-set', onSet);
  }, []);
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('logistics-archived-sync', { detail: { tab: activeTab, value: archiveFilterByTab[activeTab] ?? 'active' } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, archiveFilterByTab]);

  // Vehicles only ever rendered as cards — every other list-with-a-grid page
  // (e.g. Bundles) has a list/table alternative, and
  // this was the one left without it.
  const [vehicleViewMode, setVehicleViewMode] = useState<'grid' | 'table'>('grid');
  useEffect(() => {
    const onSet = (e: Event) => setVehicleViewMode((e as CustomEvent).detail as 'grid' | 'table');
    window.addEventListener('vehicle-view-mode', onSet);
    return () => window.removeEventListener('vehicle-view-mode', onSet);
  }, []);

  const [driverViewMode, setDriverViewMode] = useState<'grid' | 'table'>('grid');
  useEffect(() => {
    const onSet = (e: Event) => setDriverViewMode((e as CustomEvent).detail as 'grid' | 'table');
    window.addEventListener('driver-view-mode', onSet);
    return () => window.removeEventListener('driver-view-mode', onSet);
  }, []);

  // Load vehicles, drivers, and assignments from the API (with localStorage
  // fallback). Guest movements/schedule live on the assignment record itself
  // now — there is no separate UI-only dataset to load.
  const [loadErrors, setLoadErrors] = useState<Partial<Record<LogisticsLoadKind, unknown>>>(getLogisticsLoadErrors());
  const [retrying, setRetrying] = useState(false);
  // False until the first load has finished, so an id that is merely not
  // loaded yet is not reported as "not found".
  const [loaded, setLoaded] = useState(false);
  const loadAll = useCallback(async () => {
    setRetrying(true);
    try {
      const [v, d, a] = await Promise.all([refreshVehiclesFromApi(), refreshDriversFromApi(), refreshAssignmentsFromApi()]);
      setVehicles(v);
      setDrivers(d);
      setAssignments(a);
    } finally {
      setRetrying(false);
      setLoaded(true);
    }
  }, []);

  // A failed load is shown inside this screen (never as a toast); the stale rows stay.
  useEffect(() => {
    const onState = () => setLoadErrors(getLogisticsLoadErrors());
    window.addEventListener(LOGISTICS_LOAD_STATE_EVENT, onState);
    return () => window.removeEventListener(LOGISTICS_LOAD_STATE_EVENT, onState);
  }, []);

  useEffect(() => {
    void loadAll();
    setGuestOptions(loadGuests().map(g => ({ id: g.id, name: g.name })));
  }, []);

  // ── Stats ─────────────────────────────────────────────────────────────────

  const stats: LogisticsStats = {
    totalVehicles: vehicles.length,
    assignedVehicles: vehicles.filter(v => v.status === 'assigned' || v.status === 'in-transit').length,
    totalGuests: assignments.reduce((sum, a) => sum + 1 + a.plus_ones, 0),
    assignedGuests: assignments.filter(a => a.status !== 'cancelled').reduce((sum, a) => sum + 1 + a.plus_ones, 0),
    totalDrivers: drivers.length,
  };

  // ── Trigger from global header ────────────────────────────────────────────

  React.useEffect(() => {
    if (triggerAddVehicle) {
      openAddVehicle();
      onAddVehicleHandled?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAddVehicle]);

  React.useEffect(() => {
    if (triggerAddDriver) {
      openAddDriver();
      onAddDriverHandled?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAddDriver]);

  React.useEffect(() => {
    if (triggerAddAssignment) { openAddAssignment(); onAddAssignmentHandled?.(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAddAssignment]);

  // Notify parent when assignment form opens/closes
  React.useEffect(() => {
    onAssignmentFormOpenChange?.(showAssignmentModal, !!editingAssignment);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssignmentModal, editingAssignment]);

  // Sync status override from header into local form state
  React.useEffect(() => {
    if (assignmentStatusOverride !== undefined) {
      setAssignmentForm(prev => ({ ...prev, status: assignmentStatusOverride as typeof prev.status }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentStatusOverride]);

  // Listen for header Save/Cancel buttons when assignment form is open
  React.useEffect(() => {
    if (!showAssignmentModal) return;
    const onSave = () => saveAssignment();
    const onCancel = () => setShowAssignmentModal(false);
    window.addEventListener('form-save', onSave);
    window.addEventListener('form-cancel', onCancel);
    return () => {
      window.removeEventListener('form-save', onSave);
      window.removeEventListener('form-cancel', onCancel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssignmentModal, assignmentForm]);

  // Listen for header Archive button when assignment form is open
  React.useEffect(() => {
    if (!showAssignmentModal || !editingAssignment) return;
    const onArchive = () => { archiveAssignment(editingAssignment.id); setShowAssignmentModal(false); };
    window.addEventListener('logistics-assignment-archive', onArchive);
    return () => window.removeEventListener('logistics-assignment-archive', onArchive);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssignmentModal, editingAssignment]);

  // ── Route-based vehicle form ─────────────────────────────────────────────

  React.useEffect(() => {
    if (vehicleAction === 'new') {
      setEditingVehicle(null);
      setVehicleForm(blankVehicle());
      setShowVehicleModal(true);
      window.dispatchEvent(new CustomEvent('vehicle-status-init', { detail: 'available' }));
      window.dispatchEvent(new CustomEvent('vehicle-archived-init', { detail: false }));
    } else if (vehicleAction === 'edit' && vehicleIdProp) {
      const found = vehicles.find(v => v.id === vehicleIdProp);
      if (found) {
        setEditingVehicle(found);
        setVehicleForm({ ...found });
        setShowVehicleModal(true);
        window.dispatchEvent(new CustomEvent('vehicle-status-init', { detail: found.status }));
        window.dispatchEvent(new CustomEvent('vehicle-archived-init', { detail: !!found.archived }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleAction, vehicleIdProp, vehicles.length]);

  React.useEffect(() => {
    const found = vehicleAction === 'edit' && vehicleIdProp ? vehicles.find(v => v.id === vehicleIdProp) : undefined;
    onVehicleNameChange?.(found?.name ?? null);
  }, [vehicleAction, vehicleIdProp, vehicles, onVehicleNameChange]);

  // ── Vehicle photo (sidebar) — same generic attachments store used by
  // VendorFormPage/FabricFormPage/etc (entity_type + entity_id), so a
  // vehicle photo needs no new `vehicles` column or backend route. ─────────
  React.useEffect(() => {
    if (vehicleAction === 'edit' && vehicleIdProp) {
      getAttachments('vehicle', vehicleIdProp)
        .then(atts => setVehicleImages(atts.map(a => ({ id: a.id, url: a.url }))))
        .catch(() => setVehicleImages([]));
    } else {
      setVehicleImages([]);
    }
  }, [vehicleAction, vehicleIdProp]);

  const handleVehicleImageFile = async (file: File) => {
    const entityId = vehicleIdProp || editingVehicle?.id;
    if (!entityId) return;
    setVehicleImageUploading(true);
    try {
      const att = await uploadImage(file, { entityType: 'vehicle', entityId, label: vehicleForm.name || 'Vehicle photo' });
      setVehicleImages(imgs => [...imgs, { id: att.id, url: att.url }]);
    } catch (err) {
      console.error('Vehicle photo upload failed:', err);
    }
    setVehicleImageUploading(false);
  };

  const handleVehicleImageRemove = async () => {
    const current = vehicleImages[0];
    if (!current) return;
    setVehicleImages(imgs => imgs.slice(1));
    try {
      await deleteAttachment(current.id);
    } catch (err) {
      console.error('Vehicle photo removal failed:', err);
    }
  };

  // ── Route-based driver form ──────────────────────────────────────────────

  React.useEffect(() => {
    if (driverAction === 'edit' && driverIdProp) {
      const found = drivers.find(d => d.id === driverIdProp);
      if (found) {
        setEditingDriver(found);
        setDriverForm({ ...found });
        setShowDriverModal(true);
        window.dispatchEvent(new CustomEvent('driver-archived-init', { detail: !!found.archived }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverAction, driverIdProp, drivers.length]);

  React.useEffect(() => {
    const found = driverAction === 'edit' && driverIdProp ? drivers.find(d => d.id === driverIdProp) : undefined;
    onDriverNameChange?.(found?.name ?? null);
  }, [driverAction, driverIdProp, drivers, onDriverNameChange]);

  // ── Driver photo (sidebar) — same generic attachments store as the
  // vehicle photo above. ───────────────────────────────────────────────────
  React.useEffect(() => {
    if (driverAction === 'edit' && driverIdProp) {
      getAttachments('driver', driverIdProp)
        .then(atts => setDriverImages(atts.map(a => ({ id: a.id, url: a.url }))))
        .catch(() => setDriverImages([]));
    } else {
      setDriverImages([]);
    }
  }, [driverAction, driverIdProp]);

  const handleDriverImageFile = async (file: File) => {
    const entityId = driverIdProp || editingDriver?.id;
    if (!entityId) return;
    setDriverImageUploading(true);
    try {
      const att = await uploadImage(file, { entityType: 'driver', entityId, label: driverForm.name || 'Driver photo' });
      setDriverImages(imgs => [...imgs, { id: att.id, url: att.url }]);
    } catch (err) {
      console.error('Driver photo upload failed:', err);
    }
    setDriverImageUploading(false);
  };

  const handleDriverImageRemove = async () => {
    const current = driverImages[0];
    if (!current) return;
    setDriverImages(imgs => imgs.slice(1));
    try {
      await deleteAttachment(current.id);
    } catch (err) {
      console.error('Driver photo removal failed:', err);
    }
  };

  // ── Breadcrumb vehicle status / save events ──────────────────────────────
  const saveVehicleRef = React.useRef<() => void>(() => {});
  const saveDriverRef = React.useRef<() => void>(() => {});

  React.useEffect(() => {
    const statusHandler = (e: Event) =>
      setVehicleForm(p => ({ ...p, status: (e as CustomEvent<string>).detail as Vehicle['status'] }));
    const saveHandler = () => saveVehicleRef.current();
    const driverSaveHandler = () => saveDriverRef.current();
    window.addEventListener('vehicle-status-change', statusHandler);
    window.addEventListener('vehicle-form-save', saveHandler);
    window.addEventListener('driver-form-save', driverSaveHandler);
    return () => {
      window.removeEventListener('vehicle-status-change', statusHandler);
      window.removeEventListener('vehicle-form-save', saveHandler);
      window.removeEventListener('driver-form-save', driverSaveHandler);
    };
  }, []);

  // ── Vehicle CRUD ──────────────────────────────────────────────────────────

  const openAddVehicle = () => {
    // Route to /vehicle/new rather than opening the modal state directly —
    // Status now lives in the Header breadcrumb (case 'vehicle-new'/
    // 'vehicle-detail'), which only renders for the routed view, so this
    // needs to match the same path onAddVehicle (Header's "New Vehicle"
    // button, wired in App.tsx) already takes.
    navigate('/vehicle/new');
  };

  const openEditVehicle = (v: Vehicle) => {
    if (vehicleAction) {
      navigate(`/vehicle/${fmtId('VH', v.id)}`);
      return;
    }
    setEditingVehicle(v);
    setVehicleForm({ ...v });
    setShowVehicleModal(true);
  };

  const saveVehicle = () => {
    if (!vehicleForm.name.trim() || !vehicleForm.license_plate.trim()) return;
    if (editingVehicle) {
      const saved = svcUpdateVehicle(editingVehicle.id, vehicleForm);
      if (saved) setVehicles(prev => prev.map(v => v.id === editingVehicle.id ? saved : v));
    } else {
      const created = svcCreateVehicle(vehicleForm);
      setVehicles(prev => [...prev, created]);
    }
    setShowVehicleModal(false);
    // Navigate back to logistics when in route-based mode — preserve
    // whichever tab the user came from rather than bouncing to the
    // /logistics/schedule default (see App.tsx's bare-/logistics redirect).
    if (vehicleAction) {
      navigate(`/logistics/${LOGISTICS_TAB_SLUGS[activeTab]}`);
    }
  };
  saveVehicleRef.current = saveVehicle;

  const archiveVehicle = (id: string) => {
    const saved = svcUpdateVehicle(id, { archived: true });
    if (saved) setVehicles(prev => prev.map(v => v.id === id ? saved : v));
  };

  const restoreVehicle = (id: string) => {
    const saved = svcUpdateVehicle(id, { archived: false });
    if (saved) setVehicles(prev => prev.map(v => v.id === id ? saved : v));
  };

  const deleteVehicle = (id: string) => {
    const vehicle = vehicles.find(v => v.id === id);
    if (!canDeleteForever(vehicle)) return;
    foreverVehicle.request(vehicle!);
  };

  // Archive/Restore/Delete triggered from the breadcrumb (Header.tsx,
  // 'vehicle-detail' case) — same event-bridge convention as
  // vehicle-status-change/vehicle-form-save above, since Header and this
  // route-based vehicle page aren't in the same React tree.
  React.useEffect(() => {
    const toggleHandler = (e: Event) => {
      if (!vehicleIdProp) return;
      const archived = (e as CustomEvent<boolean>).detail;
      if (archived) archiveVehicle(vehicleIdProp);
      else restoreVehicle(vehicleIdProp);
    };
    const deleteRequestHandler = () => {
      if (!vehicleIdProp) return;
      const vehicle = vehicles.find(v => v.id === vehicleIdProp);
      if (canDeleteForever(vehicle)) foreverVehicle.request(vehicle!);
    };
    window.addEventListener('vehicle-archive-toggle', toggleHandler);
    window.addEventListener('vehicle-delete-request', deleteRequestHandler);
    return () => {
      window.removeEventListener('vehicle-archive-toggle', toggleHandler);
      window.removeEventListener('vehicle-delete-request', deleteRequestHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleIdProp, vehicles]);

  // ── Driver CRUD ───────────────────────────────────────────────────────────

  const openAddDriver = () => {
    navigate('/driver/new');
  };

  const openEditDriver = (d: Driver) => {
    navigate(`/driver/${fmtId('DR', d.id)}`);
  };

  const saveDriver = () => {
    if (!driverForm.name.trim() || !driverForm.phone.trim()) return;
    if (editingDriver) {
      const saved = svcUpdateDriver(editingDriver.id, driverForm);
      if (saved) setDrivers(prev => prev.map(d => d.id === editingDriver.id ? saved : d));
    } else {
      const created = svcCreateDriver(driverForm);
      setDrivers(prev => [...prev, created]);
    }
    setShowDriverModal(false);
    if (driverAction) {
      navigate(`/logistics/${LOGISTICS_TAB_SLUGS[activeTab]}`);
    }
  };
  saveDriverRef.current = saveDriver;

  const archiveDriver = (id: string) => {
    const saved = svcUpdateDriver(id, { archived: true });
    if (saved) setDrivers(prev => prev.map(d => d.id === id ? saved : d));
  };

  const restoreDriver = (id: string) => {
    const saved = svcUpdateDriver(id, { archived: false });
    if (saved) setDrivers(prev => prev.map(d => d.id === id ? saved : d));
  };

  const deleteDriver = (id: string) => {
    const driver = drivers.find(d => d.id === id);
    if (!canDeleteForever(driver)) return;
    foreverDriver.request(driver!);
  };

  // Archive/Restore/Delete triggered from the breadcrumb (Header.tsx,
  // 'driver-detail' case) — same event-bridge convention as
  // driver-form-save above, since Header and this route-based driver page
  // aren't in the same React tree.
  React.useEffect(() => {
    const toggleHandler = (e: Event) => {
      if (!driverIdProp) return;
      const archived = (e as CustomEvent<boolean>).detail;
      if (archived) archiveDriver(driverIdProp);
      else restoreDriver(driverIdProp);
    };
    const deleteRequestHandler = () => {
      if (!driverIdProp) return;
      const driver = drivers.find(d => d.id === driverIdProp);
      if (canDeleteForever(driver)) foreverDriver.request(driver!);
    };
    window.addEventListener('driver-archive-toggle', toggleHandler);
    window.addEventListener('driver-delete-request', deleteRequestHandler);
    return () => {
      window.removeEventListener('driver-archive-toggle', toggleHandler);
      window.removeEventListener('driver-delete-request', deleteRequestHandler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverIdProp, drivers]);

  // ── Assignment CRUD ───────────────────────────────────────────────────────

  const openAddAssignment = () => {
    setEditingAssignment(null);
    setAssignmentForm(blankAssignment());
    setPickGuestId('');
    setShowAssignmentModal(true);
  };

  const addGuestToAssignment = (guestId: string) => {
    if (!guestId) return;
    setAssignmentForm(p => ({ ...p, guest_ids: [...new Set([...(p.guest_ids ?? []), guestId])] }));
    setPickGuestId('');
  };

  const removeGuestFromAssignment = (guestId: string) => {
    setAssignmentForm(p => ({ ...p, guest_ids: (p.guest_ids ?? []).filter(id => id !== guestId) }));
  };

  const openEditAssignment = (a: GuestAssignment) => {
    setEditingAssignment(a);
    setAssignmentForm({ ...a, guest_ids: a.guest_ids ?? [] });
    setPickGuestId('');
    setShowAssignmentModal(true);
  };

  const saveAssignment = () => {
    const hasGuests = (assignmentForm.guest_ids?.length ?? 0) > 0 || assignmentForm.guest_name.trim();
    if (!hasGuests || !assignmentForm.vehicle_id || !assignmentForm.pickup_location.trim()) return;
    if (editingAssignment) {
      const saved = svcUpdateAssignment(editingAssignment.id, assignmentForm);
      if (saved) setAssignments(prev => prev.map(a => a.id === editingAssignment.id ? saved : a));
    } else {
      const created = svcCreateAssignment(assignmentForm);
      setAssignments(prev => [...prev, created]);
    }
    setShowAssignmentModal(false);
  };

  const archiveAssignment = (id: string) => {
    const saved = svcUpdateAssignment(id, { archived: true });
    if (saved) setAssignments(prev => prev.map(a => a.id === id ? saved : a));
  };

  const restoreAssignment = (id: string) => {
    const saved = svcUpdateAssignment(id, { archived: false });
    if (saved) setAssignments(prev => prev.map(a => a.id === id ? saved : a));
  };

  const deleteAssignment = (id: string) => {
    const assignment = assignments.find(a => a.id === id);
    if (!canDeleteForever(assignment)) return;
    foreverAssignment.request(assignment!);
  };

  const matchesArchiveFilter = (archived: boolean | undefined, filter: ArchiveFilter) => {
    if (filter === 'all') return true;
    if (filter === 'archived') return !!archived;
    return !archived;
  };
  const visibleVehicles = vehicles.filter(v => matchesArchiveFilter(v.archived, archiveFilterByTab.vehicles ?? 'active'));
  const visibleDrivers = drivers.filter(d => matchesArchiveFilter(d.archived, archiveFilterByTab.drivers ?? 'active'));
  const visibleAssignments = assignments.filter(a => matchesArchiveFilter(a.archived, archiveFilterByTab.assignments ?? 'active'));

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getVehicleName = (id: string) => vehicles.find(v => v.id === id)?.name ?? '—';
  const getVehicleCapacityUsed = (vehicleId: string) =>
    assignments.filter(a => a.vehicle_id === vehicleId && a.status !== 'cancelled')
      .reduce((sum, a) => sum + 1 + a.plus_ones, 0);

  // pickup_time/dropoff_time are full datetime-local strings
  // ("2026-11-28T11:00") now, not bare "HH:MM" — still handle the bare form
  // so any not-yet-migrated record doesn't crash instead of just misreading.
  const formatTime = (t: string) => {
    if (!t) return '—';
    try {
      const [datePart, timePart] = t.includes('T') ? t.split('T') : [null, t];
      const [h, m] = timePart.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const time = `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
      if (!datePart) return time;
      const date = new Date(`${datePart}T00:00:00`);
      return `${formatDayMonth(date)}, ${time}`;
    } catch { return t; }
  };

  // Export button (Header): each tab registers its own visible rows; Header
  // exports the tab that is open.
  const logisticsExportCtx = {
    vehicleTypeLabel: (t: string) => VEHICLE_TYPE_LABELS[t as VehicleType] ?? t,
    driverName: (id?: string) => drivers.find(d => d.id === id)?.name ?? '',
    vehicleName: (id?: string) => vehicles.find(v => v.id === id)?.name ?? '',
    time: formatTime,
  };
  useExport('logistics-vehicles', { rows: visibleVehicles, ctx: logisticsExportCtx });
  useExport('logistics-drivers', { rows: visibleDrivers, ctx: logisticsExportCtx });
  useExport('logistics-assignments', { rows: visibleAssignments, ctx: logisticsExportCtx });

  // ── Render ────────────────────────────────────────────────────────────────

  // Schedule (assignments) Timeline/Table view — toggle lives in Header's
  // toolbar (same header-toolbar-tabs pattern as the asoebi Bundles
  // grid/table toggle), reaching this component via a window event since
  // Header and LogisticsManager are siblings under App.tsx.
  React.useEffect(() => {
    const onSync = (e: Event) => setAssignmentView((e as CustomEvent).detail as AssignmentViewMode);
    window.addEventListener('logistics-assignment-view-mode', onSync);
    return () => window.removeEventListener('logistics-assignment-view-mode', onSync);
  }, []);

  // A vehicle URL whose id is not in the loaded list (deleted, mistyped, or the
  // list failed to load) must say so; falling through to the form branch below
  // used to render an empty page.
  if (vehicleAction === 'edit' && !showVehicleModal) {
    const known = vehicles.some(v => v.id === vehicleIdProp);
    if (!known) {
      const backToVehicles = () => navigate(`/logistics/${LOGISTICS_TAB_SLUGS.vehicles}`);
      return (
        <div className="wt-container logistics-manager fade-in">
          {!loaded ? (
            <EmptyState icon="list" title="Loading vehicle" compact />
          ) : loadErrors.vehicles !== undefined ? (
            <LoadErrorBox what="vehicles" error={loadErrors.vehicles} onRetry={() => void loadAll()} retrying={retrying} />
          ) : (
            <EmptyState
              icon="list"
              title="Vehicle not found"
              description="This vehicle may have been deleted, or the link is wrong."
              action={<button type="button" className="primary-btn" onClick={backToVehicles}>Back to vehicles</button>}
            />
          )}
        </div>
      );
    }
  }

  // When vehicleAction is set OR Add Vehicle modal is open, render only the vehicle form as a full page
  if (vehicleAction || showVehicleModal) {
    const assignedDriver = drivers.find(d => d.id === vehicleForm.driver_id);
    return (
      <div className="wt-container logistics-manager fade-in">
        {showVehicleModal && (
          <div className="detail-layout">
            <div className="inline-form-page">
              <VehicleFormFields
                form={vehicleForm}
                onChange={setVehicleForm}
                drivers={drivers}
              />
              <RichTextEditor
                label="Notes"
                placeholder="Any additional information..."
                value={vehicleForm.notes ?? ''}
                onChange={val => setVehicleForm(p => ({ ...p, notes: val }))}
                rows={3}
              />
            </div>
            <aside className="detail-sidebar">
              <SidebarPhotoCard
                url={vehicleImages.length > 0 ? resolveMediaUrl(vehicleImages[0].url) : undefined}
                alt={vehicleForm.name || 'Vehicle'}
                uploading={vehicleImageUploading}
                onFileSelect={handleVehicleImageFile}
                onRemove={handleVehicleImageRemove}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleVehicleImageFile(f); }}
              />
              <section className="side-section">
                <h4>Driver</h4>
                {assignedDriver ? (
                  <>
                    <div className="stat-row"><span>Name</span><strong>{assignedDriver.name}</strong></div>
                    <div className="stat-row"><span>Phone</span><strong>{assignedDriver.phone}</strong></div>
                  </>
                ) : (
                  <p className="rpf-sidebar-empty">No driver assigned yet.</p>
                )}
              </section>
            </aside>
          </div>
        )}
        <ConfirmDialog
          {...foreverVehicle.dialogProps}
          title="Delete this vehicle forever?"
          subject={foreverVehicle.target ? `${foreverVehicle.target.name} (${foreverVehicle.target.license_plate})` : undefined}
          description="This cannot be undone."
          consequences={['The vehicle and its photos', 'Its notes']}
          confirmLabel="Delete forever"
        />
      </div>
    );
  }

  if (showDriverModal) {
    const assignedVehicle = vehicles.find(v => v.id === driverForm.vehicle_id);
    return (
      <div className="wt-container logistics-manager fade-in">
        <div className="detail-layout">
          <div className="inline-form-page">
            <div className="form-row form-row--split">
              <FormInput label="Full Name" type="text" placeholder="Driver's name" value={driverForm.name} onChange={e => setDriverForm(p => ({ ...p, name: e.target.value }))} required />
              <FormInput label="Phone" type="tel" placeholder={PHONE_PLACEHOLDER} value={driverForm.phone} onChange={e => setDriverForm(p => ({ ...p, phone: e.target.value }))} required />
            </div>
            <div className="form-row form-row--split">
              <FormInput label="Email" type="email" placeholder="driver@email.com" value={driverForm.email} onChange={e => setDriverForm(p => ({ ...p, email: e.target.value }))} />
              <div className="form-group">
                <label className="form-label">Assigned Vehicle</label>
                <SearchableSelect value={driverForm.vehicle_id ?? ''} onChange={value => setDriverForm(p => ({ ...p, vehicle_id: value }))} options={[{ value: '', label: '— No vehicle —' }, ...pickerOptions(vehicles, driverForm.vehicle_id, v => `${v.name} (${v.license_plate})`)]} />
              </div>
            </div>
            <div className="form-row form-row--split">
              <FormInput label="Emergency Contact Name" type="text" placeholder="Contact name" value={driverForm.emergency_contact} onChange={e => setDriverForm(p => ({ ...p, emergency_contact: e.target.value }))} />
              <FormInput label="Emergency Contact Phone" type="tel" placeholder={PHONE_PLACEHOLDER} value={driverForm.emergency_phone} onChange={e => setDriverForm(p => ({ ...p, emergency_phone: e.target.value }))} />
            </div>
            <RichTextEditor label="Notes / Instructions" placeholder="Special instructions, route notes..." value={driverForm.notes ?? ''} onChange={val => setDriverForm(p => ({ ...p, notes: val }))} rows={2} />
          </div>
          <aside className="detail-sidebar">
            <SidebarPhotoCard
              url={driverImages.length > 0 ? resolveMediaUrl(driverImages[0].url) : undefined}
              alt={driverForm.name || 'Driver'}
              uploading={driverImageUploading}
              onFileSelect={handleDriverImageFile}
              onRemove={handleDriverImageRemove}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleDriverImageFile(f); }}
            />
            <section className="side-section">
              <h4>Assigned Vehicle</h4>
              {assignedVehicle ? (
                <>
                  <div className="stat-row"><span>Name</span><strong>{assignedVehicle.name}</strong></div>
                  <div className="stat-row"><span>Plate</span><strong>{assignedVehicle.license_plate}</strong></div>
                </>
              ) : (
                <p className="rpf-sidebar-empty">No vehicle assigned yet.</p>
              )}
            </section>
          </aside>
        </div>
      </div>
    );
  }

  if (showAssignmentModal) {
    const assignedGuestIds = new Set(assignmentForm.guest_ids ?? []);
    const pickableGuests = guestOptions.filter(g => !assignedGuestIds.has(g.id));
    const assignedGuestObjs = (assignmentForm.guest_ids ?? [])
      .map(id => guestOptions.find(g => g.id === id))
      .filter((g): g is { id: string; name: string } => !!g);

    // Driver is derived from the assigned vehicle — there is no driver_id
    // on GuestAssignment itself, so this is a read-only lookup rather than an
    // editable field, and it stays blank until a vehicle is picked.
    const assignedVehicle = vehicles.find(v => v.id === assignmentForm.vehicle_id);
    const assignedDriver = assignedVehicle?.driver_id ? drivers.find(d => d.id === assignedVehicle.driver_id) : undefined;

    return (
      <div className="wt-container logistics-manager fade-in">
        <div className="detail-layout">
          <div className="inline-form-page">
            <div className="form-group">
              <label className="form-label">Movement Type</label>
              <SearchableSelect
                value={assignmentForm.movement_type ?? 'pickup'}
                onChange={value => setAssignmentForm(p => ({ ...p, movement_type: value as MovementType }))}
                options={[{ value: 'pickup', label: 'Pickup' }, { value: 'dropoff', label: 'Drop-off' }, { value: 'transfer', label: 'Transfer' }]}
              />
            </div>
            <div className="form-row form-row--split">
              <FormInput label="Pickup Location" type="text" placeholder="Street address or landmark" value={assignmentForm.pickup_location} onChange={e => setAssignmentForm(p => ({ ...p, pickup_location: e.target.value }))} required />
              <FormInput label="Pickup Date &amp; Time" type="datetime-local" value={assignmentForm.pickup_time} onChange={e => setAssignmentForm(p => ({ ...p, pickup_time: e.target.value }))} />
            </div>
            <div className="form-row form-row--split">
              <FormInput label="Drop-off Location" type="text" placeholder="Venue, hotel, or address" value={assignmentForm.dropoff_location} onChange={e => setAssignmentForm(p => ({ ...p, dropoff_location: e.target.value }))} />
              <FormInput label="Drop-off Date &amp; Time" type="datetime-local" value={assignmentForm.dropoff_time} onChange={e => setAssignmentForm(p => ({ ...p, dropoff_time: e.target.value }))} />
            </div>
            <div className="form-row form-row--split">
              <div className="form-group">
                <label>Assigned Vehicle <span className="form-required">*</span></label>
                <SearchableSelect
                  value={assignmentForm.vehicle_id}
                  onChange={value => setAssignmentForm(p => ({ ...p, vehicle_id: value }))}
                  options={[
                    { value: '', label: '— Select a vehicle —' },
                    ...pickerOptions(vehicles, assignmentForm.vehicle_id, v => `${v.name} — ${v.capacity - getVehicleCapacityUsed(v.id)} seats available`),
                  ]}
                />
              </div>
              <FormInput label="Group Label (optional)" type="text" placeholder="e.g. Alfa &amp; Osim Families — for a group that isn't individually on the guest list" value={assignmentForm.guest_name} onChange={e => setAssignmentForm(p => ({ ...p, guest_name: e.target.value }))} />
            </div>
            {/* Read-only — see assignedDriver above. Contact Phone was removed
                from this form entirely: the linked driver already carries a
                phone number, shown in the sidebar, so a second one typed in
                per-movement was a duplicate that could drift out of sync. */}
            <div className="form-group">
              <label className="form-label">Driver</label>
              <p className="form-hint">
                {assignmentForm.vehicle_id
                  ? (assignedDriver ? assignedDriver.name : 'No driver assigned to this vehicle yet.')
                  : 'Assign a vehicle to see its driver.'}
              </p>
            </div>
            <RichTextEditor label="Notes" placeholder="Special needs, luggage, instructions..." value={assignmentForm.notes ?? ''} onChange={val => setAssignmentForm(p => ({ ...p, notes: val }))} rows={3} />
            <div className="form-group">
              <label className="form-label">Assigned Guests</label>
              {assignedGuestObjs.length > 0 && (
                <div className="lm-guest-chips">
                  {assignedGuestObjs.map(g => (
                    <span className="lm-guest-chip" key={g.id}>
                      {g.name}
                      <button type="button" onClick={() => removeGuestFromAssignment(g.id)} aria-label={`Remove ${g.name}`}>
                        <Icon name="x" size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="lm-guest-picker">
                <SearchableSelect
                  value={pickGuestId}
                  onChange={setPickGuestId}
                  options={[{ value: '', label: 'Search guests…' }, ...pickableGuests.map(g => ({ value: g.id, label: g.name }))]}
                />
                <button type="button" className="secondary-btn" disabled={!pickGuestId} onClick={() => addGuestToAssignment(pickGuestId)}>
                  <Icon name="plus" size={14} /> Add
                </button>
              </div>
              <FormInput label="Plus Ones (additional guests)" type="number" min={0} max={20} value={assignmentForm.plus_ones} onChange={e => setAssignmentForm(p => ({ ...p, plus_ones: Number(e.target.value) }))} />
            </div>
          </div>
          <div className="detail-sidebar">
            <div className="side-section">
              <h4>Movement Summary</h4>
              <div className="stat-row"><span>Status</span><strong>{assignmentForm.status}</strong></div>
              <div className="stat-row"><span>Guests</span><strong>{(assignmentForm.guest_ids?.length ?? 0) + (assignmentForm.plus_ones || 0)}</strong></div>
              <div className="stat-row"><span>Vehicle</span><strong>{getVehicleName(assignmentForm.vehicle_id) }</strong></div>
            </div>
            {/* Requested so the driver's own contact details are reachable
                from this form without a per-movement Contact Phone field —
                same info, read once from the driver record instead of typed
                in and risking drift from it. */}
            {assignedDriver && (
              <div className="side-section">
                <h4>Driver</h4>
                <div className="stat-row"><span>Name</span><strong>{assignedDriver.name}</strong></div>
                <div className="stat-row"><span>Phone</span><strong>{assignedDriver.phone || '—'}</strong></div>
                {assignedDriver.email && <div className="stat-row"><span>Email</span><strong>{assignedDriver.email}</strong></div>}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wt-container logistics-manager fade-in">
      {Object.keys(loadErrors).length > 0 && (
        <LoadErrorBox
          what={(Object.keys(loadErrors) as LogisticsLoadKind[]).join(', ')}
          error={Object.values(loadErrors)[0]}
          onRetry={() => void loadAll()}
          retrying={retrying}
        />
      )}
      <section className="stats-grid">
        <MetricCard label="Total Vehicles" value={stats.totalVehicles} tone="blue" icon="list" />
        <MetricCard label="Arrangements" value={stats.assignedVehicles} tone="green" icon="check" />
        <MetricCard label="Guests Assigned" value={stats.assignedGuests} tone="purple" icon="users" />
        <MetricCard label="Drivers" value={stats.totalDrivers} tone="red" icon="user" />
      </section>

      {/* ── VEHICLES TAB ── */}
      {activeTab === 'vehicles' && (
        <>
          {visibleVehicles.length === 0 ? (
            <EmptyState
              icon="list"
              title={archiveFilterByTab.vehicles === 'archived' ? 'No archived vehicles' : 'No vehicles added yet'}
              description={archiveFilterByTab.vehicles === 'archived' ? 'Vehicles you archive will show up here.' : 'Add your fleet to start assigning guests to vehicles.'}
              action={archiveFilterByTab.vehicles !== 'archived' ? <button className="primary-btn" onClick={openAddVehicle}><Icon name="plus" size={16} /> Add First Vehicle</button> : undefined}
            />
          ) : vehicleViewMode === 'table' ? (
            <DataTable
              recordList={{ kind: 'vehicle' }}
              data={visibleVehicles}
              onRowClick={vehicle => navigate(`/vehicle/${fmtId('VH', vehicle.id)}`)}
              columns={[
                idColumn('vehicle'),
                { key: 'name', header: 'Name', render: v => v.name },
                { key: 'type', header: 'Type', render: v => VEHICLE_TYPE_LABELS[v.type as VehicleType] ?? v.type },
                { key: 'license_plate', header: 'License Plate', render: v => v.license_plate || '—' },
                { key: 'color', header: 'Color', render: v => v.color || '—' },
                {
                  key: 'driver_id',
                  header: 'Driver',
                  render: v => {
                    const driver = drivers.find(d => d.id === v.driver_id);
                    return driver ? driver.name : 'Not assigned';
                  },
                },
                {
                  key: 'capacity',
                  header: 'Capacity',
                  render: v => `${getVehicleCapacityUsed(v.id)} / ${v.capacity} seats`,
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: v => (
                    <StatusBadge variant={
                      v.status === 'available' ? 'success' :
                      v.status === 'assigned' ? 'info' :
                      v.status === 'in-transit' ? 'info' :
                      v.status === 'maintenance' ? 'neutral' : 'warning'
                    }>
                      {(v.status ?? 'unknown').replace('-', ' ')}
                    </StatusBadge>
                  ),
                },
              ]}
            />
          ) : (
            <div className="vehicles-grid">
              {visibleVehicles.map(vehicle => {
                const driver = drivers.find(d => d.id === vehicle.driver_id);
                const used = getVehicleCapacityUsed(vehicle.id);
                const pct = Math.min(100, Math.round((used / vehicle.capacity) * 100));
                // ContentCard's status.type only has 4 values — 'available'
                // is the only genuinely positive state, everything else
                // (assigned/in-transit/maintenance/unknown) reads as
                // 'pending'. The exact original label still goes into
                // status.label so the badge text itself doesn't lose detail.
                const statusType: 'active' | 'pending' = vehicle.status === 'available' ? 'active' : 'pending';

                return (
                  <ContentCard
                    key={vehicle.id}
                    hideImage
                    onClick={() => navigate(`/vehicle/${fmtId('VH', vehicle.id)}`)}
                    title={vehicle.name}
                    subtitle={VEHICLE_TYPE_LABELS[vehicle.type]}
                    status={{ label: (vehicle.status ?? 'unknown').replace('-', ' '), type: statusType }}
                    description={vehicle.notes || undefined}
                    details={[
                      { label: 'License Plate', value: vehicle.license_plate || '—' },
                      { label: 'Color', value: vehicle.color || '—' },
                    ]}
                    tags={vehicle.features.length > 0 ? vehicle.features : undefined}
                    footerStats={[{ icon: 'user', label: driver ? driver.name : 'Not assigned' }]}
                    customContent={
                      <div className="capacity-section">
                        <div className="capacity-header">
                          <span className="capacity-label">Capacity</span>
                          <span className="capacity-value">{used} / {vehicle.capacity} seats</span>
                        </div>
                        <div className="capacity-bar">
                          <div
                            className="capacity-fill"
                            style={ {
                              width: `${pct}%`,
                              background: pct >= 100 ? '#ef4444' : pct >= 75 ? '#f59e0b' : '#10b981'
                            }}
                          />
                        </div>
                      </div>
                    }
                    customActions={vehicle.archived ? (
                      <div className="logistics-card-actions" onClick={e => e.stopPropagation()}>
                        <button className="logistics-action-btn" onClick={() => restoreVehicle(vehicle.id)}>
                          <Icon name="refresh" size={13} /> Restore
                        </button>
                        <button className="logistics-action-btn danger" onClick={() => deleteVehicle(vehicle.id)}>
                          <Icon name="trash" size={13} /> Delete
                        </button>
                      </div>
                    ) : undefined}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── ASSIGNMENTS TAB — the single, API-backed source of truth for guest→
          vehicle movements. Creating a movement (pickup/dropoff/transfer,
          time, location) and assigning the guest happen together in the same
          form (see showAssignmentModal below); the timeline view below is
          the same real `assignments` data, just presented the way the old
          disconnected localStorage-only Schedule tab used to. ── */}
      {activeTab === 'assignments' && (
        <>
          {visibleAssignments.length === 0 ? (
            <EmptyState
              icon="users"
              title={archiveFilterByTab.assignments === 'archived' ? 'No archived movements' : 'No movements scheduled yet'}
              description={archiveFilterByTab.assignments === 'archived' ? 'Movements you archive will show up here.' : 'Assign a guest to a vehicle to schedule their pickup, drop-off, or transfer.'}
              action={archiveFilterByTab.assignments !== 'archived' ? <button className="primary-btn" onClick={openAddAssignment}><Icon name="plus" size={16} /> Add Movement</button> : undefined}
            />
          ) : assignmentView === 'timeline' ? (
            <div className="schedule-timeline">
              {[...visibleAssignments]
                .sort((a, b) => (a.pickup_time || '').localeCompare(b.pickup_time || ''))
                .map((item) => {
                  const movementType = item.movement_type ?? 'pickup';
                  const guestLabel = item.guests?.length
                    ? item.guests.map(g => g.name).join(', ')
                    : item.guest_name;
                  return (
                    <div
                      key={item.id}
                      className="timeline-content timeline-content--clickable"
                      onClick={() => openEditAssignment(item)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="timeline-header">
                        <div className="timeline-time">
                          <Icon name="clock" size={14} />
                          {formatTime(item.pickup_time)}
                        </div>
                        <span className="ds-badge ds-badge-dynamic" style={{ /* ds-dynamic: movement type color from palette */ '--ds-badge-accent': movementTypeColors[movementType] } as React.CSSProperties}>
                          {movementType}
                        </span>
                        {item.status === 'cancelled' ? (
                          <span className="ds-badge ds-badge-dynamic" style={{ /* ds-dynamic: assignment status color from palette */ '--ds-badge-accent': assignmentStatusColors[item.status] } as React.CSSProperties}>
                            Cancelled
                          </span>
                        ) : (
                          <MiniStatusStepper status={item.status} />
                        )}
                      </div>
                      <div className="timeline-vehicle">{getVehicleName(item.vehicle_id)}</div>
                      <div className="timeline-location">
                        <Icon name="mapPin" size={13} />
                        {item.pickup_location}
                        {item.dropoff_location && ` → ${item.dropoff_location}`}
                      </div>
                      <div className="timeline-guests">
                        <Icon name="users" size={13} />
                        {guestLabel}{item.plus_ones > 0 ? ` (+${item.plus_ones})` : ''}
                      </div>
                      {item.notes && <p className="timeline-notes">{item.notes}</p>}
                      {item.archived && (
                        <div className="timeline-actions">
                          <button className="logistics-action-btn" onClick={e => { e.stopPropagation(); restoreAssignment(item.id); }}>
                            <Icon name="refresh" size={13} /> Restore
                          </button>
                          <button className="logistics-action-btn danger" onClick={e => { e.stopPropagation(); deleteAssignment(item.id); }}>
                            <Icon name="x" size={13} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <DataTable
              data={visibleAssignments}
              onRowClick={row => openEditAssignment(row)}
              columns={[
                idColumn<GuestAssignment>('transportAssignment'),
                {
                  key: 'guest_name',
                  header: 'Guest',
                  sortable: true,
                  render: (value: string, row: any) => (
                    <div className="vendor-info">
                      <div className="vendor-text">
                        <span className="vendor-primary-name">{value}</span>
                        {row.guest_phone && <span className="vendor-subtext">{row.guest_phone}</span>}
                      </div>
                    </div>
                  )
                },
                {
                  key: 'vehicle_id',
                  header: 'Vehicle',
                  sortable: false,
                  render: (value: string) => <span className="ds-badge ds-badge-neutral">{getVehicleName(value)}</span>
                },
                {
                  key: 'movement_type',
                  header: 'Movement',
                  sortable: true,
                  render: (value: MovementType | undefined) => (
                    <span className="ds-badge ds-badge-dynamic" style={{ /* ds-dynamic: movement type color from palette */ '--ds-badge-accent': movementTypeColors[value ?? 'pickup'] } as React.CSSProperties}>
                      {value ?? 'pickup'}
                    </span>
                  )
                },
                {
                  key: 'pickup_location',
                  header: 'Pickup Location',
                  sortable: true,
                  render: (value: string) => (
                    <div style={ { display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Icon name="mapPin" size={12} />
                      {value}
                    </div>
                  )
                },
                {
                  key: 'pickup_time',
                  header: 'Pickup Time',
                  sortable: true,
                  render: (value: string) => formatTime(value)
                },
                {
                  key: 'dropoff_location',
                  header: 'Drop-off',
                  sortable: false,
                  render: (value: string) => value || '—'
                },
                {
                  key: 'plus_ones',
                  header: 'Guests',
                  sortable: true,
                  render: (value: number) => <span className="ds-badge ds-badge-neutral">{1 + value}</span>
                },
                {
                  key: 'status',
                  header: 'Status',
                  sortable: true,
                  render: (value: AssignmentStatus) => (
                    <span className="ds-badge ds-badge-dynamic" style={{ /* ds-dynamic: assignment status color from palette */ '--ds-badge-accent': assignmentStatusColors[value] } as React.CSSProperties}>
                      {value === 'cancelled' ? 'Cancelled' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </span>
                  )
                }
              ]}
              emptyMessage={archiveFilterByTab.assignments === 'archived' ? 'No archived movements' : 'No movements scheduled'}
              hoverable={true}
            />
          )}
        </>
      )}

      {/* ── DRIVERS TAB ── */}
      {activeTab === 'drivers' && (
        <>
          {visibleDrivers.length === 0 ? (
            <EmptyState
              icon="user"
              title={archiveFilterByTab.drivers === 'archived' ? 'No archived drivers' : 'No drivers added'}
              description={archiveFilterByTab.drivers === 'archived' ? 'Drivers you archive will show up here.' : 'Add driver contact info for day-of coordination.'}
              action={archiveFilterByTab.drivers !== 'archived' ? <button className="primary-btn" onClick={openAddDriver}><Icon name="plus" size={16} /> Add First Driver</button> : undefined}
            />
          ) : driverViewMode === 'table' ? (
            <DataTable
              recordList={{ kind: 'driver' }}
              data={visibleDrivers}
              onRowClick={driver => openEditDriver(driver)}
              columns={[
                idColumn('driver'),
                { key: 'name', header: 'Name', render: d => d.name },
                { key: 'phone', header: 'Phone', render: d => d.phone },
                { key: 'email', header: 'Email', render: d => d.email || '—' },
                {
                  key: 'vehicle_id',
                  header: 'Vehicle',
                  render: d => {
                    const vehicle = vehicles.find(v => v.id === d.vehicle_id);
                    return vehicle ? vehicle.name : 'Not assigned';
                  },
                },
                {
                  key: 'emergency_contact',
                  header: 'Emergency Contact',
                  render: d => d.emergency_contact ? `${d.emergency_contact}${d.emergency_phone ? `: ${d.emergency_phone}` : ''}` : '—',
                },
              ]}
            />
          ) : (
            <div className="drivers-grid">
              {visibleDrivers.map(driver => {
                const assignedVehicle = vehicles.find(v => v.id === driver.vehicle_id);
                return (
                  <ContactCard
                    key={driver.id}
                    initials={driver.name.charAt(0).toUpperCase()}
                    name={driver.name}
                    subtitle={assignedVehicle ? assignedVehicle.name : undefined}
                    metaRows={[
                      { icon: 'phone', label: driver.phone, href: `tel:${driver.phone}` },
                      ...(driver.email ? [{ icon: 'mail', label: driver.email, href: `mailto:${driver.email}` }] : []),
                      ...(driver.emergency_contact ? [{ icon: 'alert-triangle', label: `${driver.emergency_contact}: ${driver.emergency_phone}` }] : []),
                    ]}
                    customContent={driver.notes ? <p className="driver-notes">{driver.notes}</p> : undefined}
                    onClick={() => openEditDriver(driver)}
                    // Archive now lives on the driver detail page (Header's
                    // 'driver-detail' actions), matching the pattern already
                    // used there — no need to duplicate it here per card.
                    actions={driver.archived ? [
                      { icon: 'refresh', onClick: () => restoreDriver(driver.id), title: 'Restore' },
                      { icon: 'trash', onClick: () => deleteDriver(driver.id), title: 'Delete', className: 'danger' },
                    ] : undefined}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════ MODALS ═══════════════════════════════ */}

      {/* Vehicle form rendered as early-return full-page above (showVehicleModal) */}
      {/* Driver/Assignment/Schedule/Transport forms rendered as early-return full-page above */}

      <ConfirmDialog
        {...foreverVehicle.dialogProps}
        title="Delete this vehicle forever?"
        subject={foreverVehicle.target ? `${foreverVehicle.target.name} (${foreverVehicle.target.license_plate})` : undefined}
        description="This cannot be undone."
        consequences={['The vehicle and its photos', 'Its notes']}
        confirmLabel="Delete forever"
      />
      <ConfirmDialog
        {...foreverDriver.dialogProps}
        title="Delete this driver forever?"
        subject={foreverDriver.target?.name}
        description="This cannot be undone."
        consequences={['The driver and their contact details', 'Their photo and notes']}
        confirmLabel="Delete forever"
      />
      <ConfirmDialog
        {...foreverAssignment.dialogProps}
        title="Delete this movement forever?"
        subject={foreverAssignment.target?.guest_name}
        description="This cannot be undone."
        consequences={['The pickup and drop-off details for this movement']}
        confirmLabel="Delete forever"
      />
    </div>
  );
};

// ── Re-exports for external callers ────────────────────────────────────────
// These were previously inline; now delegated to LogisticsService.

export {
  refreshVehiclesFromApi    as refreshLogisticsVehiclesFromApi,
  refreshDriversFromApi     as refreshLogisticsDriversFromApi,
  refreshAssignmentsFromApi as refreshLogisticsAssignmentsFromApi,
  syncLogisticsToApi,
} from '../services/LogisticsService';

export default LogisticsManager;
